import clsx from 'clsx'
import { Button } from 'component/Button/Button'
import { ConfirmModal } from 'component/ConfirmModal/ConfirmModal'
import { Loader } from 'component/Loader'
import { Modal } from 'component/Modal/Modal'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo/Typo'
import { Vertical } from 'component/Vertical/Vertical'
import { useCombineTryOn } from 'hook/useCombineTryOn'
import { useConfirm } from 'hook/useConfirm'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { ComboCart } from 'page/catalog/ComboCart/ComboCart'
import { ComboCartMobile } from 'page/catalog/ComboCart/ComboCartMobile'
import { SelectedItems } from 'page/catalog/ComboCart/SelectedItems'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { comboCartStore } from 'page/catalog/store/ComboCartStore'
import { useState } from 'react'
import { ui } from 'store/home/ui/HomeUiStore'
import { ComboCartFiltersModal } from './ComboCartFiltersModal'
import css from './ComboCartModal.module.scss'
import { GenerationModal } from './GenerationModal'
import { SearchInput } from './SearchInput'

export const ComboCartModal = observer(() => {
    const { mobile } = ui.window
    const [activeFiltersCount, setActiveFiltersCount] = useState(0)
    const [showFiltersModal, setShowFiltersModal] = useState(false)
    const fullscreen = mobile
    const demo = useDemo()
    const { stand } = demo
    const [showSearch, setShowSearch] = useState(false)
    const [currentStep, setCurrentStep] = useState<'top' | 'bottom'>('top')
    const [searchValue, setSearchValue] = useState('')
    const [showGenerationModal, setShowGenerationModal] = useState(false)
    const clearCartConfirm = useConfirm()

    const handleCombineComplete = useLatestCallback(() => {
      // Не закрываем корзину, если в ней больше 1 топ или больше 1 боттом
      const hasMultipleTop = comboCartStore.topItems.length > 1
      const hasMultipleBottom = comboCartStore.bottomItems.length > 1
      
      if (!hasMultipleTop && !hasMultipleBottom) {
        comboCartStore.open = false
      }
    })

    const { isGenerating, onCombine } = useCombineTryOn({
      topItemId: comboCartStore.selectedTop,
      bottomItemId: comboCartStore.selectedBottom,
      onComplete: handleCombineComplete,
    })

    useEffectOnce(() => {
      setSearchValue(comboCartStore.topFilters.search)
      setShowFiltersModal(false)
    })

    const handleSearchChange = (value: string) => {
      setSearchValue(value)
      if (currentStep === 'top') {
        comboCartStore.setTopSearch(value)
      } else {
        comboCartStore.setBottomSearch(value)
      }
    }

    const handleStepChange = (step: 'top' | 'bottom') => {
      setCurrentStep(step)
      if (step === 'top') {
        setSearchValue(comboCartStore.topFilters.search)
      } else {
        setSearchValue(comboCartStore.bottomFilters.search)
      }
    }

    const handleClearCart = () => {
      clearCartConfirm.confirm(
        () => {
          comboCartStore.clearAll()
        },
        {
          message: i18n('combo.ClearCartConfirm'),
          confirmText: i18n('combo.ClearCart'),
          cancelText: i18n('combo.Cancel'),
        },
      )
    }

    const handleClose = () => {
      comboCartStore.open = false
    }

    const handleGenerationModalClose = () => {
      setShowGenerationModal(false)
    }

    const canCombine = comboCartStore.canCombine

    return (
      <>
        <Modal
          onCancel={handleClose}
          className={css.modal}
          white
          fullscreen={fullscreen}
          closeButton={false}
          oh
        >
          <div
            className={clsx(
              css.header,
              fullscreen && css.fullscreenHeader,
              showSearch && css.showSearch,
            )}
          >
            <Space gap={12}>
              {!mobile && (
                <div className={css.headerIcon}>
                  <Icon name="shopping_bag" size={20} />
                </div>
              )}
              <Vertical gap={4}>
                <Typo size={16} weight={600}>
                  {i18n('combo.ComboCart')}
                </Typo>
                {!mobile && (
                  <Typo size={12} secondary>
                    {i18n('combo.SelectItemsToCombine')}
                  </Typo>
                )}
              </Vertical>
            </Space>
            <Space>
              {mobile && (
                <>
                  <Button
                    className={clsx(css.button, css.filterButton)}
                    action={() => setShowFiltersModal(true)}
                  >
                    <Space gap={6}>
                      <Icon name="filter" size={16} />
                      {activeFiltersCount > 0 && (
                        <div className={css.filterBadge}>
                          <Typo size={11}>{activeFiltersCount}</Typo>
                        </div>
                      )}
                    </Space>
                  </Button>
                  {!stand && <Button
                    className={css.button}
                    action={() => {
                      setShowSearch(!showSearch)
                      if (showSearch) {
                        handleSearchChange('')
                      }
                    }}
                  >
                    <Icon name="search" size={16} />
                  </Button>}
                </>
              )}
              {mobile && <div className={css.separator} />}
              <Button
                className={clsx(css.button, { [css.buttonClear]: !mobile })}
                action={handleClearCart}
                text={!mobile}
              >
                {!mobile ? (
                  i18n('combo.ClearCart')
                ) : (
                  <Icon name="delete" size={16} />
                )}
              </Button>
              <Button className={css.button} action={handleClose}>
                <Icon name="close" size={16} />
              </Button>
            </Space>
          </div>

          <div
            className={clsx(css.content, {
              [css.fullscreenContent]: fullscreen,
            })}
          >
            {mobile ? (
              <>
                {showSearch && (
                  <div className={css.mobileSearch}>
                    <SearchInput
                      value={searchValue}
                      onChange={handleSearchChange}
                      placeholder={i18n('Search')}
                      onCancel={() => setShowSearch(false)}
                    />
                  </div>
                )}
                <ComboCartMobile
                  onCombine={onCombine}
                  onFiltersCountChange={setActiveFiltersCount}
                  onStepChange={handleStepChange}
                  isGenerating={isGenerating}
                />
              </>
            ) : (
              <ComboCart />
            )}
          </div>

          {!mobile && (
            <div
              className={clsx(css.footer, fullscreen && css.fullscreenFooter)}
            >
              <SelectedItems />
              <Space>
                <Button xl primary action={onCombine} disabled={!canCombine || isGenerating}>
                  <Space gap={8}>
                    {isGenerating && <Loader />}
                    {isGenerating ? i18n('combo.GeneratingOutfit') : i18n('combo.CombineOutfit')}
                  </Space>
                </Button>
              </Space>
            </div>
          )}

          {mobile && showFiltersModal && (
            <ComboCartFiltersModal
              open={showFiltersModal}
              onClose={() => setShowFiltersModal(false)}
              currentStep={currentStep}
            />
          )}
        </Modal>

        <GenerationModal
          open={showGenerationModal}
          onClose={handleGenerationModalClose}
        />

        <ConfirmModal
          open={clearCartConfirm.state.open}
          onClose={clearCartConfirm.close}
          onConfirm={clearCartConfirm.handleConfirm}
          message={clearCartConfirm.state.message}
          confirmText={clearCartConfirm.state.confirmText}
          cancelText={clearCartConfirm.state.cancelText}
          confirmVariant={clearCartConfirm.state.confirmVariant}
        />
      </>
    )
  },
)
